import React from 'react';
import { Box, Dialog, IconButton, Skeleton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRounded from '@mui/icons-material/CloseRounded';

export interface ModalProps {
  open: boolean;
  /** Esc, a click outside, or the close button. */
  onClose: () => void;
  title: React.ReactNode;
  /** One line under the title: what this shows. */
  subtitle?: React.ReactNode;
  /** Controls in the header, before the close button — Download, Pin to dashboard. */
  actions?: React.ReactNode;
  /** Buttons along the bottom — Cancel, Save. */
  footer?: React.ReactNode;
  /**
   * `sm` 480px — a short form or a question. `md` 720px — the default.
   * `lg` 1080px — a table or a longer form. `full` — nearly the whole screen,
   * for a chart or table shown large; its content can fill the height.
   */
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** While something is saving: nothing closes it. */
  busy?: boolean;
  /**
   * `false` — only the window's own buttons close it: no X, and neither Esc
   * nor a click outside does anything. For a decision that has to be made,
   * e.g. reloading onto a new version. Use it sparingly: a window with no way
   * out is a trap unless one of its buttons is a way out.
   */
  dismissible?: boolean;
  /**
   * Blurs the page behind as well as dimming it — the work stays visible but
   * plainly out of reach. For a window that must be answered rather than
   * worked around.
   */
  backdropBlur?: boolean;
  /**
   * The content is still on its way: the window opens at once, title and
   * controls live, and the body shimmers at `loadingHeight` until it lands —
   * as `Panel` does.
   */
  loading?: boolean;
  /** Height the loading placeholder holds, px. Match the content so the window does not jump. */
  loadingHeight?: number;
  children: React.ReactNode;
}

const WIDTH = { sm: 480, md: 720, lg: 1080 } as const;
/**
 * A window keeps one height, whatever is in it, so switching tabs or loading
 * more does not make it jump; the content scrolls inside. `sm` — a short
 * question — fits its content instead: at a fixed height it would be mostly
 * empty. Never taller than the screen allows.
 */
const HEIGHT = { md: 640, lg: 820 } as const;

/**
 * A window over the page: a title with its controls, the content, and
 * buttons along the bottom when there are decisions to make.
 *
 * - Closes with Esc, a click outside, or the close button — except while
 *   `busy`, so a save in flight is never thrown away.
 * - On a phone it takes the whole screen.
 * - The content scrolls on its own, so the header and buttons stay in view.
 * - `md` and `lg` keep one height (`HEIGHT`), so changing what is shown — a
 *   tab, a longer list — never resizes the window. `sm` fits its content.
 * - `full` is for showing something large — an expanded chart — and lets its
 *   content fill the height: give that content `height: 100%` or `flex: 1`.
 *
 * For a panel's "Expand", pass `expanded` to `Panel` rather than building
 * this by hand. Older dialogs built on `AppDialog` move here when their
 * pages are next changed.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  actions,
  footer,
  size = 'md',
  busy = false,
  dismissible = true,
  backdropBlur = false,
  loading = false,
  loadingHeight = 240,
  children,
}: ModalProps) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const full = size === 'full';
  const titleId = React.useId();
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={open}
      // No handler at all when it cannot be dismissed: MUI then ignores a
      // click outside, and Esc is turned off beside it.
      onClose={dismissible ? close : undefined}
      disableEscapeKeyDown={!dismissible}
      fullScreen={phone}
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{
        backdrop: backdropBlur ? { sx: { backdropFilter: 'blur(4px)' } } : undefined,
        paper: {
          sx: (t) => ({
            display: 'flex',
            flexDirection: 'column',
            m: phone ? 0 : 3,
            width: phone ? '100%' : full ? `calc(100vw - ${t.spacing(6)})` : '100%',
            maxWidth: phone ? '100%' : full ? 'none' : WIDTH[size as keyof typeof WIDTH],
            height: phone ? undefined
              : full ? `calc(100vh - ${t.spacing(6)})`
                : size === 'sm' ? undefined : `min(${HEIGHT[size as keyof typeof HEIGHT]}px, calc(100vh - ${t.spacing(6)}))`,
            maxHeight: phone ? '100%' : `calc(100vh - ${t.spacing(6)})`,
            bgcolor: t.tokens.surface.raised,
            backgroundImage: 'none',
            border: phone ? 0 : `1px solid ${t.tokens.border.subtle}`,
            borderRadius: phone ? 0 : `${t.ds.radius.lg}px`,
            boxShadow: t.tokens.shadow.md,
          }),
        },
      }}
    >
      <Box
        sx={(t) => ({
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          px: 2.5,
          py: 1.75,
          flexShrink: 0,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
        })}
      >
        {/* The title keeps a readable width; controls that do not fit beside
            it drop to a row of their own, and wrap there, rather than
            squeezing the title to a word a line. Close stays top right. */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 1.5, rowGap: 1 }}>
          <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
            <Typography variant="h5" component="h2" id={titleId}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, mt: 0.25 })}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
              {actions}
            </Box>
          )}
        </Box>
        {dismissible && (
          <Tooltip title="Close">
            <span>
              <IconButton size="small" aria-label="Close" onClick={close} disabled={busy} sx={(t) => ({ color: t.tokens.text.secondary })}>
                <CloseRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', p: 2.5, display: 'flex', flexDirection: 'column' }}>
        {loading ? <Skeleton variant="rounded" height={loadingHeight} aria-label="Loading" /> : children}
      </Box>

      {footer && (
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            px: 2.5,
            py: 1.5,
            flexShrink: 0,
            borderTop: `1px solid ${t.tokens.border.subtle}`,
          })}
        >
          {footer}
        </Box>
      )}
    </Dialog>
  );
}
